import type { ReactNode } from "react";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";
import { Ago, EmptyBlock, FetchingDot, Meter, SectionCard, SeverityIcon } from "../../components/primitives";
import { formatCount } from "../../format";
import type { PodsSection } from "../../types";
import { StatTile, SubHeading } from "../backend/stat-tile";
import type { Tone } from "../backend/tone";

const KNOWN_SOURCES = ["autoscaler", "static", "custom"];

/** Pool-level state: health, concurrency, retry policy, keeper, autoscaler updates, config vs pool. */
export function PoolStrip({
  section,
  generatedAt,
  fetching,
}: {
  section: PodsSection;
  generatedAt: number | null;
  fetching: boolean;
}) {
  const { t, tp } = useLocalization();
  const pool = section.pool;
  const running = section.pods.map((pod) => pod.endpoint);

  const actions = (
    <>
      <FetchingDot active={fetching} />
      {generatedAt && (
        <span className="text-xs text-muted-foreground">
          {t("monitoring.pods.updated")} <Ago ts={generatedAt} />
        </span>
      )}
    </>
  );

  if (!pool) {
    return (
      <SectionCard title={t("monitoring.pods.pool.title")} description={t("monitoring.pods.pool.description")} actions={actions}>
        <div className="flex flex-col gap-4">
          <EmptyBlock>{t("monitoring.pods.pool.none")}</EmptyBlock>
          <ConfigComparison section={section} running={running} source={null} />
        </div>
      </SectionCard>
    );
  }

  const unhealthy = pool.total - pool.healthy;
  const podsTone: Tone = pool.total === 0 || pool.healthy === 0 ? "nok" : unhealthy > 0 ? "warn" : "ok";
  const saturated = pool.max_concurrency > 0 && pool.in_flight >= pool.max_concurrency;
  const keeper404 = section.pods.filter((pod) => pod.keeper_404).length;
  const sourceKey = KNOWN_SOURCES.includes(pool.source) ? pool.source : null;
  const update = pool.last_update;

  return (
    <SectionCard title={t("monitoring.pods.pool.title")} description={t("monitoring.pods.pool.description")} actions={actions}>
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-8">
          <StatTile
            label={t("monitoring.pods.tile.pods")}
            tone={podsTone}
            value={pool.total ? t("monitoring.pods.tile.podsValue", { healthy: pool.healthy, total: pool.total }) : t("monitoring.pods.tile.noPods")}
            sub={
              pool.total === 0
                ? t("monitoring.pods.tile.noPodsSub")
                : unhealthy > 0
                  ? tp("monitoring.pods.tile.unhealthy", unhealthy)
                  : t("monitoring.pods.tile.allHealthy")
            }
          />
          <StatTile
            label={t("monitoring.pods.tile.inFlight")}
            tone={saturated ? "warn" : "neutral"}
            value={formatCount(pool.in_flight)}
            sub={t("monitoring.pods.tile.inFlightSub", { max: pool.max_concurrency })}
          >
            <Meter
              value={pool.max_concurrency ? pool.in_flight / pool.max_concurrency : 0}
              tone={saturated ? "warn" : "neutral"}
              className="mt-1"
            />
          </StatTile>
          <StatTile
            label={t("monitoring.pods.tile.queued")}
            tone={pool.queued > 0 ? "warn" : "neutral"}
            value={formatCount(pool.queued)}
            sub={t("monitoring.pods.tile.queuedSub")}
          />
          {pool.attempt_timeouts_s?.length ? (
            <StatTile
              label={t("monitoring.pods.tile.attempts")}
              value={`${pool.attempt_timeouts_s.map(formatCount).join(" → ")} s`}
              sub={`${tp("monitoring.pods.tile.attemptsCount", pool.max_attempts)} · ${t("monitoring.pods.tile.attemptsSub", { total: formatCount(pool.request_timeout_s) })}`}
              hint={t("monitoring.pods.tile.attemptsHint")}
            />
          ) : (
            <StatTile
              label={t("monitoring.pods.tile.timeout")}
              value={`${formatCount(pool.request_timeout_s)} s`}
              sub={t("monitoring.pods.tile.timeoutSub", { attempts: pool.max_attempts })}
            />
          )}
          <StatTile
            label={t("monitoring.pods.tile.keeper")}
            tone={keeper404 > 0 ? "warn" : "neutral"}
            value={pool.keeper_enabled ? t("monitoring.pods.on") : t("monitoring.pods.off")}
            sub={keeper404 > 0 ? tp("monitoring.pods.tile.keeper404", keeper404) : t("monitoring.pods.tile.keeperSub")}
            hint={t("monitoring.pods.tile.keeperHint")}
          />
          <StatTile
            label={t("monitoring.pods.tile.session")}
            value={pool.session_open ? t("monitoring.pods.tile.sessionOpen") : t("monitoring.pods.tile.sessionClosed")}
            sub={t("monitoring.pods.tile.sessionSub")}
          />
          <StatTile
            label={t("monitoring.pods.tile.source")}
            value={sourceKey ? t(`monitoring.pods.source.${sourceKey}` as TranslationKey) : pool.source}
            sub={sourceKey ? t(`monitoring.pods.source.${sourceKey}Sub` as TranslationKey) : undefined}
          />
          <StatTile
            label={t("monitoring.pods.tile.lastUpdate")}
            value={<Ago ts={update?.at} fallback={t("monitoring.pods.tile.lastUpdateNever")} />}
            sub={
              update
                ? t("monitoring.pods.tile.lastUpdateSub", {
                    added: update.added.length,
                    removed: update.removed.length,
                    total: update.total,
                  })
                : pool.source === "static"
                  ? t("monitoring.pods.tile.lastUpdateStatic")
                  : t("monitoring.pods.tile.lastUpdateWaiting")
            }
          />
        </div>

        {update && (update.added.length > 0 || update.removed.length > 0) && (
          <div className="flex flex-col gap-2">
            <SubHeading>
              {t("monitoring.pods.update.title")} · <Ago ts={update.at} className="normal-case" />
            </SubHeading>
            <div className="flex flex-col gap-1.5 text-xs">
              {update.added.length > 0 && (
                <EndpointList label={t("monitoring.pods.update.added")} sign="+" endpoints={update.added} />
              )}
              {update.removed.length > 0 && (
                <EndpointList label={t("monitoring.pods.update.removed")} sign="−" endpoints={update.removed} />
              )}
            </div>
          </div>
        )}

        <ConfigComparison section={section} running={running} source={pool.source} />
      </div>
    </SectionCard>
  );
}

/** pod_servers (the autoscaler's snapshot) against the pods the pool actually runs */
function ConfigComparison({
  section,
  running,
  source,
}: {
  section: PodsSection;
  running: string[];
  source: string | null;
}) {
  const { t } = useLocalization();
  const configured = section.configured;
  const localities = new Map(section.configured_docs.map((doc) => [`${doc.ip}:${doc.port}`, doc.locality ?? ""]));

  let body: ReactNode;
  if (configured === null) {
    body = <p className="text-xs text-muted-foreground">{t("monitoring.pods.config.unavailable")}</p>;
  } else if (source === null) {
    body = <p className="text-xs text-muted-foreground">{t("monitoring.pods.config.noPool")}</p>;
  } else if (source !== "autoscaler") {
    body = (
      <p className="text-xs text-muted-foreground">
        {source === "static"
          ? t("monitoring.pods.config.static", { configured: configured.length })
          : t("monitoring.pods.config.notUsed", { configured: configured.length })}
      </p>
    );
  } else {
    const missing = configured.filter((endpoint) => !running.includes(endpoint));
    const extra = running.filter((endpoint) => !configured.includes(endpoint));
    const mismatch = missing.length > 0 || extra.length > 0;
    body = mismatch ? (
      <div className="flex flex-col gap-2 rounded-md border border-amber-500/35 px-3 py-2 text-xs">
        <div className="flex items-start gap-2 text-amber-300">
          <SeverityIcon severity="warning" className="mt-px" />
          <span className="min-w-0 break-words">{t("monitoring.pods.config.mismatch")}</span>
        </div>
        {missing.length > 0 && (
          <EndpointList label={t("monitoring.pods.config.missing")} endpoints={missing} localities={localities} />
        )}
        {extra.length > 0 && <EndpointList label={t("monitoring.pods.config.extra")} endpoints={extra} />}
      </div>
    ) : (
      <p className="text-xs text-muted-foreground">{t("monitoring.pods.config.match")}</p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <SubHeading>{t("monitoring.pods.config.title")}</SubHeading>
      <p className="text-xs tabular-nums">
        {t("monitoring.pods.config.summary", { configured: configured?.length ?? "–", running: running.length })}
      </p>
      {body}
    </div>
  );
}

function EndpointList({
  label,
  endpoints,
  sign,
  localities,
}: {
  label: string;
  endpoints: string[];
  sign?: string;
  localities?: Map<string, string>;
}) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      {endpoints.map((endpoint) => (
        <span key={endpoint} className="break-all rounded border border-border/70 px-1.5 py-px font-mono text-[11px]">
          {sign && <span className="mr-0.5 text-muted-foreground">{sign}</span>}
          {endpoint}
          {localities?.get(endpoint) && <span className="ml-1 text-muted-foreground">{localities.get(endpoint)}</span>}
        </span>
      ))}
    </div>
  );
}
